import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import axios from 'axios';
import { Button } from '../../Button/button';
import './TabContent.css';
import AlertPopup from './AlertPopup';
import ConfirmationModal from './ConfirmationModal';

const AvailabilityTab = ({ userDetails, onUserUpdate }) => {
    const daysOfWeek = [
        { value: 0, label: 'Sunday' },
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' }
    ];

    const [availability, setAvailability] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableServices, setAvailableServices] = useState([]);
    const [editing, setEditing] = useState(null);
    const [viewMode, setViewMode] = useState('recurrent'); // 'recurrent' or 'single'
    const [filterMode, setFilterMode] = useState('day'); // 'day', 'week', 'month'
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [collapsedDays, setCollapsedDays] = useState(() => {
        // Initialize all days as collapsed by default
        const allDaysCollapsed = {};
        daysOfWeek.forEach(day => {
            allDaysCollapsed[day.label] = true;
        });
        return allDaysCollapsed;
    }); // Track which days are collapsed
    const [showCreateForm, setShowCreateForm] = useState(false); // Track form visibility
    const [alert, setAlert] = useState({ message: '', type: 'info' });
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, itemId: null, itemName: '' });

    // Get services as array of {id, name, duration}
    const userServices = (userDetails.userServices || []).map(us => ({
        id: us.service?.id,
        name: us.service?.name,
        duration: us.duration || 60
    }));

    // Toggle collapse state for a specific day
    const toggleDayCollapse = (dayName) => {
        setCollapsedDays(prev => ({
            ...prev,
            [dayName]: !prev[dayName]
        }));
    };

    // Check if a day is collapsed
    const isDayCollapsed = (dayName) => {
        return collapsedDays[dayName] === true;
    };

    // Collapse all days
    const collapseAllDays = () => {
        const allDays = {};
        sortedDays.forEach(dayName => {
            allDays[dayName] = true;
        });
        setCollapsedDays(allDays);
    };

    // Expand all days
    const expandAllDays = () => {
        setCollapsedDays({});
    };

    useEffect(() => {
        // Fetch all available services for mapping service_id to name
        axios.get(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/services`)
            .then(res => setAvailableServices(res.data))
            .catch(() => setAvailableServices([]));
    }, []);

    useEffect(() => {
        if (!userDetails?.id) return;
        fetchAvailability();
        // eslint-disable-next-line
    }, [userDetails]);

    const fetchAvailability = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch all slots for this healer
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/availability`, {
                params: { user_id: userDetails.id }
            });
            setAvailability(response.data);
        } catch (error) {
            setError('Error fetching availability.');
            setAlert({
                message: 'Failed to load availability slots. Please refresh the page.',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Helper: add minutes to a time string ("HH:mm")
    function addMinutes(time, minsToAdd) {
        const [h, m] = time.split(':').map(Number);
        const date = new Date(0, 0, 0, h, m + minsToAdd, 0, 0);
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
    }

    // Helper: compare two time strings ("HH:mm")
    function timeToMinutes(time) {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
    }

    // Helper: get week start and end dates
    const getWeekRange = (date) => {
        const start = new Date(date);
        start.setDate(start.getDate() - start.getDay());
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return { start, end };
    };

    // Helper: get month start and end dates
    const getMonthRange = (date) => {
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return { start, end };
    };

    // Filter availability based on selected mode and date
    const getFilteredAvailability = () => {
        if (!filterDate) return availability;

        // Create date in local timezone to avoid timezone issues
        const [year, month, day] = filterDate.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day); // month is 0-indexed
        let startDate, endDate;

        switch (filterMode) {
            case 'week':
                const weekRange = getWeekRange(selectedDate);
                startDate = weekRange.start;
                endDate = weekRange.end;
                break;
            case 'month':
                const monthRange = getMonthRange(selectedDate);
                startDate = monthRange.start;
                endDate = monthRange.end;
                break;
            case 'day':
            default:
                startDate = selectedDate;
                endDate = selectedDate;
                break;
        }

        return availability.filter(slot => {
            // Parse slot date in local timezone
            const [slotYear, slotMonth, slotDay] = slot.date.split('-').map(Number);
            const slotDate = new Date(slotYear, slotMonth - 1, slotDay);
            return slotDate >= startDate && slotDate <= endDate;
        });
    };

    const handleAddRecurrentAvailability = async (values, actions) => {
        setError(null);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/availability/recurrent`, {
                user_id: userDetails.id,
                service_id: values.serviceId,
                start_date: values.startDate,
                end_date: values.endDate,
                days_of_week: values.selectedDays,
                start_time: values.startTime,
                end_time: values.endTime,
                timezone: values.timezone || ""
            });

            setAlert({
                message: `Successfully created ${response.data.createdSlots} availability slots!`,
                type: 'success'
            });
            actions.resetForm();
            fetchAvailability();
        } catch (error) {
            setError('Error adding recurrent availability.');
            setAlert({
                message: 'Failed to create recurrent availability slots. Please try again.',
                type: 'error'
            });
            console.error('Error:', error.response?.data || error.message);
        }
    };

    const handleAddSingleAvailability = async (values, actions) => {
        setError(null);
        try {
            const serviceId = values.serviceId;
            const service = userServices.find(s => String(s.id) === String(serviceId));
            const duration = service ? Number(service.duration) : 60;
            let createdCount = 0;

            // Ensure the date is in the correct format (YYYY-MM-DD)
            const selectedDate = values.date;

            for (const slot of values.timeSlots) {
                const startMins = timeToMinutes(slot.start);
                const endMins = timeToMinutes(slot.end);
                if (isNaN(startMins) || isNaN(endMins) || endMins <= startMins) {
                    setAlert({
                        message: 'Invalid time range detected. Please check your time slots.',
                        type: 'error'
                    });
                    continue;
                }
                let current = slot.start;
                while (timeToMinutes(current) + duration <= endMins) {
                    const next = addMinutes(current, duration);
                    await axios.post(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/availability`, {
                        user_id: userDetails.id,
                        service_id: serviceId,
                        date: selectedDate,
                        start_time: current,
                        end_time: next,
                    });
                    createdCount++;
                    current = next;
                }
            }
            if (createdCount === 0) {
                setAlert({
                    message: 'No valid slots created. Please check your time ranges.',
                    type: 'error'
                });
            } else {
                setAlert({
                    message: `Successfully created ${createdCount} availability slot(s)!`,
                    type: 'success'
                });
            }
            actions.resetForm();
            fetchAvailability();
        } catch (error) {
            setError('Error adding availability.');
            setAlert({
                message: 'Failed to create availability slots. Please try again.',
                type: 'error'
            });
        }
    };

    const handleEditAvailability = async (values, actions) => {
        setError(null);
        try {
            await axios.put(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/availability/${editing.id}`, {
                date: values.date,
                service_id: values.serviceId,
                start_time: values.startTime,
                end_time: values.endTime,
                timezone: values.timezone || null
            });

            setAlert({
                message: 'Availability slot updated successfully!',
                type: 'success'
            });
            setEditing(null);
            fetchAvailability();
            actions.resetForm();
        } catch (error) {
            setError('Error updating availability.');
            setAlert({
                message: 'Failed to update availability slot. Please try again.',
                type: 'error'
            });
        }
    };

    const handleDeleteAvailability = async (availabilityId) => {
        setError(null);
        try {
            await axios.delete(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/availability/${availabilityId}`);
            fetchAvailability();
        } catch (error) {
            setError('Error deleting availability.');
            setAlert({
                message: 'Failed to delete availability slot. Please try again.',
                type: 'error'
            });
        }
    };

    const openDeleteModal = (availabilityId, itemName) => {
        setDeleteModal({
            isOpen: true,
            itemId: availabilityId,
            itemName: itemName
        });
    };

    const closeDeleteModal = () => {
        setDeleteModal({
            isOpen: false,
            itemId: null,
            itemName: ''
        });
    };

    const confirmDelete = () => {
        if (deleteModal.itemId) {
            handleDeleteAvailability(deleteModal.itemId);
        }
    };

    // Helper to get service name from id
    const getServiceName = (id) => {
        const found = availableServices.find(s => s.id === id);
        return found ? found.name : 'Service';
    };

    // Get filtered availability
    const filteredAvailability = getFilteredAvailability();

    // Group availability by day of week for better display
    const groupedAvailability = filteredAvailability.reduce((acc, slot) => {
        // Parse date in local timezone to avoid timezone issues
        const [slotYear, slotMonth, slotDay] = slot.date.split('-').map(Number);
        const date = new Date(slotYear, slotMonth - 1, slotDay);
        const dayOfWeek = date.getDay();
        const dayName = daysOfWeek.find(d => d.value === dayOfWeek)?.label || 'Unknown';

        if (!acc[dayName]) {
            acc[dayName] = [];
        }
        acc[dayName].push(slot);
        return acc;
    }, {});

    // Sort days in correct order
    const sortedDays = daysOfWeek.map(day => day.label).filter(day => groupedAvailability[day]);

    // Get filter display text
    const getFilterDisplayText = () => {
        // Parse date in local timezone
        const [year, month, day] = filterDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);

        switch (filterMode) {
            case 'week':
                const weekRange = getWeekRange(date);
                return `${weekRange.start.toLocaleDateString()} - ${weekRange.end.toLocaleDateString()}`;
            case 'month':
                return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            case 'day':
            default:
                return date.toLocaleDateString();
        }
    };

    return (
        <>
            <AlertPopup
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ message: '', type: 'info' })}
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDelete}
                title="Delete Availability Slot"
                message={`Are you sure you want to delete this availability slot? This action cannot be undone.`}
                confirmText="Delete Slot"
                cancelText="Cancel"
                type="danger"
            />

            <div className="tab-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem 0' }}>
                <div className="tab-header" style={{ maxWidth: 800, width: '100%', margin: '0 auto', borderBottom: 'none', marginBottom: 24 }}>
                    <h2 style={{ fontWeight: 700, fontSize: '2.1rem', color: '#222', letterSpacing: '-1px' }}>Appointment Slots</h2>
                    <p style={{ color: '#888', fontSize: '1.08rem', marginTop: '0.2rem' }}>Manage your availability and appointment slots</p>
                </div>

                {error && <div style={{ color: 'red', marginBottom: 12, maxWidth: 800, width: '100%' }}>{error}</div>}

                {loading ? (
                    <div className="loaderContainer"><div className="loader"></div></div>
                ) : (
                    <div style={{ width: '100%', maxWidth: 800, display: 'flex', flexDirection: 'column', gap: 32 }}>
                        {/* Create Slots Button */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                buttonStyle="btn--primary"
                                onClick={() => setShowCreateForm(!showCreateForm)}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '1.1rem',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(38, 168, 153, 0.2)'
                                }}
                            >
                                {showCreateForm ? 'Hide Create Form' : 'Create Slots'}
                            </Button>
                        </div>

                        {/* Edit availability form */}
                        {editing && (
                            <div className="edit-availability-section" style={{ background: '#f8f9fa', borderRadius: 16, boxShadow: '0 2px 16px rgba(38,168,153,0.07), 0 1.5px 6px rgba(0,0,0,0.03)', padding: '2rem', marginBottom: 0 }}>
                                <h3 style={{ fontWeight: 600, fontSize: '1.3rem', color: '#333', marginBottom: 18 }}>Edit Availability Slot</h3>
                                <Formik
                                    initialValues={{
                                        date: editing.date || '',
                                        serviceId: editing.service_id || '',
                                        startTime: editing.start_time || '',
                                        endTime: editing.end_time || '',
                                        timezone: editing.timezone || ''
                                    }}
                                    validate={values => {
                                        const errors = {};
                                        if (!values.date) errors.date = 'Date is required';
                                        if (!values.serviceId) errors.serviceId = 'Service is required';
                                        if (!values.startTime) errors.startTime = 'Start time is required';
                                        if (!values.endTime) errors.endTime = 'End time is required';
                                        if (values.startTime && values.endTime && timeToMinutes(values.startTime) >= timeToMinutes(values.endTime)) {
                                            errors.endTime = 'End time must be after start time';
                                        }
                                        return errors;
                                    }}
                                    onSubmit={handleEditAvailability}
                                >
                                    {({ isSubmitting, errors, touched }) => (
                                        <Form>
                                            <div className="row g-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                                                <div style={{ flex: 1, minWidth: 180 }}>
                                                    <label className="form-label">Date</label>
                                                    <Field name="date" type="date" className="form-control" required />
                                                    {errors.date && touched.date && <div className="text-danger">{errors.date}</div>}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 180 }}>
                                                    <label className="form-label">Service</label>
                                                    <Field name="serviceId" as="select" className="form-select" required>
                                                        <option value="">Select a service</option>
                                                        {availableServices.map((service) => (
                                                            <option key={service.id} value={service.id}>{service.name}</option>
                                                        ))}
                                                    </Field>
                                                    {errors.serviceId && touched.serviceId && <div className="text-danger">{errors.serviceId}</div>}
                                                </div>
                                            </div>
                                            <div className="row g-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                                                <div style={{ flex: 1, minWidth: 180 }}>
                                                    <label className="form-label">Start Time</label>
                                                    <Field name="startTime" type="time" className="form-control" required />
                                                    {errors.startTime && touched.startTime && <div className="text-danger">{errors.startTime}</div>}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 180 }}>
                                                    <label className="form-label">End Time</label>
                                                    <Field name="endTime" type="time" className="form-control" required />
                                                    {errors.endTime && touched.endTime && <div className="text-danger">{errors.endTime}</div>}
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-end gap-3 mt-4">
                                                <Button type="button" buttonStyle="btn--secondary" onClick={() => setEditing(null)}>Cancel</Button>
                                                <Button type="submit" buttonStyle="btn--primary" disabled={isSubmitting}>Update Slot</Button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        )}

                        {/* Add Availability Form - Collapsible */}
                        {showCreateForm && (
                            <div className="add-availability-section" style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(38,168,153,0.07), 0 1.5px 6px rgba(0,0,0,0.03)', padding: '2rem', marginBottom: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                                    <h3 style={{ fontWeight: 600, fontSize: '1.3rem', color: '#333', margin: 0 }}>
                                        Create New Slots
                                    </h3>
                                </div>

                                {/* View Mode Toggle */}
                                <div className="view-mode-toggle">
                                    <Button
                                        buttonStyle={viewMode === 'recurrent' ? 'btn--primary' : 'btn--secondary'}
                                        onClick={() => setViewMode('recurrent')}
                                    >
                                        Recurrent Slots
                                    </Button>
                                    <Button
                                        buttonStyle={viewMode === 'single' ? 'btn--primary' : 'btn--secondary'}
                                        onClick={() => setViewMode('single')}
                                    >
                                        Single Day
                                    </Button>
                                </div>

                                {viewMode === 'recurrent' ? (
                                    <Formik
                                        initialValues={{
                                            serviceId: '',
                                            startDate: '',
                                            endDate: '',
                                            selectedDays: [],
                                            startTime: '',
                                            endTime: '',
                                            timezone: ''
                                        }}
                                        validate={values => {
                                            const errors = {};
                                            if (!values.serviceId) errors.serviceId = 'Service is required';
                                            if (!values.startDate) errors.startDate = 'Start date is required';
                                            if (!values.endDate) errors.endDate = 'End date is required';
                                            if (!values.selectedDays.length) errors.selectedDays = 'At least one day must be selected';
                                            if (!values.startTime) errors.startTime = 'Start time is required';
                                            if (!values.endTime) errors.endTime = 'End time is required';
                                            if (values.startTime && values.endTime && timeToMinutes(values.startTime) >= timeToMinutes(values.endTime)) {
                                                errors.endTime = 'End time must be after start time';
                                            }
                                            if (values.startDate && values.endDate && new Date(values.startDate) > new Date(values.endDate)) {
                                                errors.endDate = 'End date must be after start date';
                                            }
                                            return errors;
                                        }}
                                        onSubmit={handleAddRecurrentAvailability}
                                    >
                                        {({ values, isSubmitting, errors, touched }) => (
                                            <Form>
                                                <div className="row g-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                                                    <div style={{ flex: 1, minWidth: 180 }}>
                                                        <label className="form-label">Service</label>
                                                        <Field name="serviceId" as="select" className="form-select" required>
                                                            <option value="">Select a service</option>
                                                            {availableServices.map((service) => (
                                                                <option key={service.id} value={service.id}>{service.name}</option>
                                                            ))}
                                                        </Field>
                                                        {errors.serviceId && touched.serviceId && <div className="text-danger">{errors.serviceId}</div>}
                                                    </div>
                                                </div>

                                                <div className="row g-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                                                    <div style={{ flex: 1, minWidth: 180 }}>
                                                        <label className="form-label">Start Date</label>
                                                        <Field name="startDate" type="date" className="form-control" required />
                                                        {errors.startDate && touched.startDate && <div className="text-danger">{errors.startDate}</div>}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 180 }}>
                                                        <label className="form-label">End Date</label>
                                                        <Field name="endDate" type="date" className="form-control" required />
                                                        {errors.endDate && touched.endDate && <div className="text-danger">{errors.endDate}</div>}
                                                    </div>
                                                </div>

                                                <div style={{ marginBottom: 16 }}>
                                                    <label className="form-label">Days of Week</label>
                                                    <div className="days-of-week-container">
                                                        {daysOfWeek.map((day) => (
                                                            <label key={day.value} className="day-checkbox">
                                                                <Field
                                                                    type="checkbox"
                                                                    name="selectedDays"
                                                                    value={day.value.toString()}
                                                                />
                                                                <span>{day.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {errors.selectedDays && touched.selectedDays && <div className="text-danger">{errors.selectedDays}</div>}
                                                </div>

                                                <div className="row g-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                                                    <div style={{ flex: 1, minWidth: 180 }}>
                                                        <label className="form-label">Start Time</label>
                                                        <Field name="startTime" type="time" className="form-control" required />
                                                        {errors.startTime && touched.startTime && <div className="text-danger">{errors.startTime}</div>}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 180 }}>
                                                        <label className="form-label">End Time</label>
                                                        <Field name="endTime" type="time" className="form-control" required />
                                                        {errors.endTime && touched.endTime && <div className="text-danger">{errors.endTime}</div>}
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-end mt-4" style={{ marginTop: 24 }}>
                                                    <Button type="submit" buttonStyle="btn--primary" disabled={isSubmitting}>
                                                        {isSubmitting ? 'Creating...' : 'Create Recurrent Slots'}
                                                    </Button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                ) : (
                                    <Formik
                                        initialValues={{
                                            date: '',
                                            serviceId: '',
                                            timeSlots: [{ start: '', end: '' }],
                                        }}
                                        validate={values => {
                                            const errors = {};
                                            if (!values.date) errors.date = 'Date is required';
                                            if (!values.serviceId) errors.serviceId = 'Service is required';
                                            if (!values.timeSlots || values.timeSlots.length === 0) {
                                                errors.timeSlots = 'At least one time slot is required';
                                            } else {
                                                values.timeSlots.forEach((slot, index) => {
                                                    if (!slot.start) {
                                                        if (!errors.timeSlots) errors.timeSlots = {};
                                                        errors.timeSlots[index] = 'Start time is required';
                                                    }
                                                    if (!slot.end) {
                                                        if (!errors.timeSlots) errors.timeSlots = {};
                                                        errors.timeSlots[index] = 'End time is required';
                                                    }
                                                    if (slot.start && slot.end && timeToMinutes(slot.start) >= timeToMinutes(slot.end)) {
                                                        if (!errors.timeSlots) errors.timeSlots = {};
                                                        errors.timeSlots[index] = 'End time must be after start time';
                                                    }
                                                });
                                            }
                                            return errors;
                                        }}
                                        onSubmit={handleAddSingleAvailability}
                                    >
                                        {({ values, errors, touched, isSubmitting }) => (
                                            <Form>
                                                <div className="row g-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                                                    <div style={{ flex: 1, minWidth: 180 }}>
                                                        <label className="form-label">Date</label>
                                                        <Field name="date" type="date" className="form-control" required />
                                                        {errors.date && touched.date && <div className="text-danger">{errors.date}</div>}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 180 }}>
                                                        <label className="form-label">Service</label>
                                                        <Field name="serviceId" as="select" className="form-select" required>
                                                            <option value="">Select a service</option>
                                                            {availableServices.map((service) => (
                                                                <option key={service.id} value={service.id}>{service.name}</option>
                                                            ))}
                                                        </Field>
                                                        {errors.serviceId && touched.serviceId && <div className="text-danger">{errors.serviceId}</div>}
                                                    </div>
                                                </div>
                                                <FieldArray name="timeSlots">
                                                    {({ remove, push }) => (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                                                            <label className="form-label" style={{ marginBottom: 4 }}>Time Slots</label>
                                                            {values.timeSlots && values.timeSlots.map((slot, idx) => (
                                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                                                                    <Field
                                                                        name={`timeSlots[${idx}].start`}
                                                                        type="time"
                                                                        className="form-control"
                                                                        required
                                                                        style={{ maxWidth: 120 }}
                                                                    />
                                                                    <span style={{ fontWeight: 600 }}>to</span>
                                                                    <Field
                                                                        name={`timeSlots[${idx}].end`}
                                                                        type="time"
                                                                        className="form-control"
                                                                        required
                                                                        style={{ maxWidth: 120 }}
                                                                    />
                                                                    {values.timeSlots.length > 1 && (
                                                                        <Button type="button" buttonStyle="btn--danger" buttonSize="btn--small" onClick={() => remove(idx)} style={{ marginLeft: 8 }}>Remove</Button>
                                                                    )}
                                                                    {errors.timeSlots && errors.timeSlots[idx] && (
                                                                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: 4 }}>{errors.timeSlots[idx]}</div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            <Button type="button" buttonStyle="btn--secondary" buttonSize="btn--small" onClick={() => push({ start: '', end: '' })}>
                                                                Add Time Slot
                                                            </Button>
                                                        </div>
                                                    )}
                                                </FieldArray>
                                                <div className="d-flex justify-content-end mt-4" style={{ marginTop: 24 }}>
                                                    <Button type="submit" buttonStyle="btn--primary" disabled={isSubmitting}>
                                                        {isSubmitting ? 'Adding...' : 'Add Availability'}
                                                    </Button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                )}
                            </div>
                        )}

                        {/* Current Availability Display */}
                        <div className="current-availability-section" style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(38,168,153,0.07), 0 1.5px 6px rgba(0,0,0,0.03)', padding: '2rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                                <h3 style={{ fontWeight: 600, fontSize: '1.3rem', color: '#333', margin: 0 }}>Current Availability</h3>

                                {/* Filter Controls */}
                                <div className="filter-controls">
                                    <select
                                        value={filterMode}
                                        onChange={(e) => setFilterMode(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="day">Day</option>
                                        <option value="week">Week</option>
                                        <option value="month">Month</option>
                                    </select>
                                    <input
                                        type="date"
                                        value={filterDate}
                                        onChange={(e) => setFilterDate(e.target.value)}
                                        className="filter-date"
                                    />
                                    <span className="filter-display">
                                        {getFilterDisplayText()}
                                    </span>
                                </div>
                            </div>

                            {/* Collapse/Expand Controls */}
                            {sortedDays.length > 0 && (
                                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                    <Button
                                        onClick={expandAllDays}
                                        buttonStyle="btn--outline"
                                        buttonSize="btn--small"
                                    >
                                        Expand All
                                    </Button>
                                    <Button
                                        onClick={collapseAllDays}
                                        buttonStyle="btn--outline"
                                        buttonSize="btn--small"
                                    >
                                        Collapse All
                                    </Button>
                                </div>
                            )}

                            {filteredAvailability.length > 0 ? (
                                <div className="grouped-availability">
                                    {sortedDays.map(dayName => (
                                        <div key={dayName} className="day-group">
                                            <div className="day-header" onClick={() => toggleDayCollapse(dayName)}>
                                                <Button
                                                    buttonStyle="btn--link"
                                                    buttonSize="btn--small"
                                                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', fontWeight: 600, color: '#333' }}
                                                >
                                                    {isDayCollapsed(dayName) ? '+' : '-'}
                                                </Button>
                                                {dayName}
                                            </div>
                                            {!isDayCollapsed(dayName) && (
                                                <div className="day-content">
                                                    {groupedAvailability[dayName]
                                                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                                                        .map((slot, index) => (
                                                            <div key={slot.id || index} className="slot-item">
                                                                <div className="slot-info">
                                                                    <div className="slot-service">
                                                                        <span className="slot-service-name">{getServiceName(slot.service_id)}</span>
                                                                        <span className="slot-date">
                                                                            {slot.date}
                                                                        </span>
                                                                    </div>
                                                                    <div className="slot-time">
                                                                        <strong>Time:</strong> {slot.start_time} - {slot.end_time}
                                                                    </div>
                                                                </div>
                                                                <div className="slot-actions">
                                                                    <Button
                                                                        onClick={() => setEditing(slot)}
                                                                        buttonStyle="btn--primary"
                                                                        buttonSize="btn--small"
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                    <Button
                                                                        onClick={() => openDeleteModal(slot.id, `${getServiceName(slot.service_id)} on ${slot.date}`)}
                                                                        buttonStyle="btn--danger"
                                                                        buttonSize="btn--small"
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-availability" style={{ color: '#bbb', fontSize: '1.1rem', boxShadow: 'none', padding: '2.5rem 0', background: 'none' }}>
                                    <p>No availability slots found for the selected period. Add some above!</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AvailabilityTab; 